import { Pending } from "@/types/Pending";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./auth-context";

interface PendenciasContextType {
  pendencias: Pending[];
  isLoading: boolean;
  refetchPendencias: () => Promise<void>;
  getPendenciaPorTela: (tela: string) => Pending | undefined;
  getPendenciaMaisImportante: () => Pending | undefined;
  getSidebarAlerts: () => Record<string, number>;
}

const PendenciasContext = createContext<PendenciasContextType | undefined>(
  undefined,
);

interface PendenciasProviderProps {
  children: ReactNode;
}

export function PendenciasProvider({ children }: PendenciasProviderProps) {
  const [pendencias, setPendencias] = useState<Pending[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  const fetchPendencias = useCallback(async () => {
    if (!user?.cli_codigo) {
      setPendencias([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // const data = await getPendencias(user.cli_codigo);
      // setPendencias(data);
    } catch (error) {
      console.error("Erro ao buscar pendências:", error);
      setPendencias([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.cli_codigo]);

  useEffect(() => {
    fetchPendencias();
  }, [fetchPendencias]);

  const refetchPendencias = async () => {
    await fetchPendencias();
  };

  const getPendenciaPorTela = (tela: string): Pending | undefined => {
    return pendencias.find(
      (p) =>
        p.tela.toLowerCase().includes(tela.toLowerCase()) ||
        tela.toLowerCase().includes(p.tela.toLowerCase()),
    );
  };

  const getPendenciaMaisImportante = (): Pending | undefined => {
    if (pendencias.length === 0) return undefined;

    return (
      pendencias.find((p) => p.prioridade === 1) ||
      pendencias.sort((a, b) => a.prioridade - b.prioridade)[0]
    );
  };

  const mapTelaToSidebarId = (tela: string): string | null => {
    const mapping: Record<string, string> = {
      "Dados pessoais": "dados-pessoais",
      "Cotações em aberto": "cotacoes-abertas",
      "FAQ / Suporte": "central-ajuda",
      "Histórico de transportes": "contratos",
    };

    return mapping[tela] || null;
  };

  const getSidebarAlerts = (): Record<string, number> => {
    const alerts: Record<string, number> = {};

    pendencias.forEach((pendencia) => {
      const sidebarId = mapTelaToSidebarId(pendencia.tela);
      if (sidebarId) {
        alerts[sidebarId] = (alerts[sidebarId] || 0) + pendencia.qtd;
      }
    });

    return alerts;
  };

  const value: PendenciasContextType = {
    pendencias,
    isLoading,
    refetchPendencias,
    getPendenciaPorTela,
    getPendenciaMaisImportante,
    getSidebarAlerts,
  };

  return (
    <PendenciasContext.Provider value={value}>
      {children}
    </PendenciasContext.Provider>
  );
}

export function usePendencias() {
  const context = useContext(PendenciasContext);
  if (context === undefined) {
    throw new Error("usePendencias must be used within a PendenciasProvider");
  }
  return context;
}
